import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';


const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setIsMenuOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Top-level items (Services is rendered separately as a dropdown).
  const navItems = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About' },
    { href: '/how-change-happens', label: 'How Change Happens' },
    { href: '/blog', label: 'Blog' },
    { href: '/contact', label: 'Contact' },
  ];

  // Services dropdown — the headline conditions, plus a route to the full list.
  const serviceLinks = [
    { href: '/anxiety-stress', label: 'Anxiety & Stress' },
    { href: '/radiotherapy-anxiety', label: 'Radiotherapy Anxiety' },
    { href: '/medical-procedure-anxiety', label: 'Medical Anxiety' },
    { href: '/persistent-pain', label: 'Chronic Pain' },
    { href: '/weight-mindset', label: 'Weight Mindset' },
    { href: '/ibs-hypnotherapy', label: 'IBS' },
  ];

  const isServicesActive =
    location.pathname === '/services' || serviceLinks.some((s) => s.href === location.pathname);

  const linkClass = (href: string) =>
    `transition-colors duration-200 font-medium text-sm ${
      location.pathname === href ? 'text-accent-600 font-semibold' : 'text-gray-700 hover:text-accent-600'
    }`;

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/90 backdrop-blur-sm'
      }`}
      role="banner"
    >
      <div className="container-custom">
        <div className="flex justify-between items-center py-1.5 md:py-2">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              aria-label="Sharon Clare Hypnotherapy - Home"
              onDoubleClick={(e) => {
                e.preventDefault();
                window.location.href = '/admin/login';
              }}
              title="Double‑click to open Admin"
            >
              <img
                src="/images/Sharon Clare Logo.png"
                loading="lazy"
                alt="Sharon Clare Hypnotherapy Logo"
                className="w-16 md:w-20 h-auto"
                width="80"
                height="80"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6" role="navigation" aria-label="Main navigation">
            <Link to="/" className={linkClass('/')} aria-current={location.pathname === '/' ? 'page' : undefined}>
              Home
            </Link>
            <Link to="/about" className={linkClass('/about')} aria-current={location.pathname === '/about' ? 'page' : undefined}>
              About
            </Link>

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 transition-colors duration-200 font-medium text-sm ${
                  isServicesActive ? 'text-accent-600 font-semibold' : 'text-gray-700 hover:text-accent-600'
                }`}
                onClick={() => setServicesOpen((v) => !v)}
                aria-expanded={servicesOpen}
                aria-haspopup="true"
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {servicesOpen && (
                <div className="absolute left-0 top-full pt-3 w-64" role="menu">
                  <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-2">
                    <Link
                      to="/assessment"
                      className="block rounded-lg bg-accent-50 text-accent-800 px-3 py-2 text-sm font-medium hover:bg-accent-100 mb-1"
                      role="menuitem"
                    >
                      Not sure where to start? Free assessment →
                    </Link>
                    {serviceLinks.map((s) => (
                      <Link
                        key={s.href}
                        to={s.href}
                        className={`block rounded-lg px-3 py-2 text-sm hover:bg-gray-50 ${
                          location.pathname === s.href ? 'text-accent-600 font-semibold' : 'text-gray-700'
                        }`}
                        role="menuitem"
                      >
                        {s.label}
                      </Link>
                    ))}
                    <Link
                      to="/services"
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-accent-600 hover:bg-gray-50 border-t border-gray-100 mt-1"
                      role="menuitem"
                    >
                      View all services →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/how-change-happens" className={linkClass('/how-change-happens')} aria-current={location.pathname === '/how-change-happens' ? 'page' : undefined}>
              How Change Happens
            </Link>
            <Link to="/blog" className={linkClass('/blog')} aria-current={location.pathname === '/blog' ? 'page' : undefined}>
              Blog
            </Link>
            <Link to="/contact" className={linkClass('/contact')} aria-current={location.pathname === '/contact' ? 'page' : undefined}>
              Contact
            </Link>
          </nav>

          {/* Single CTA (desktop) */}
          <div className="hidden md:flex items-center">
            <Link to="/book" className="btn-primary text-sm px-4 py-2">
              Book Free Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-1.5"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div id="mobile-menu" className="lg:hidden pb-2 border-t border-gray-200 mt-1" role="navigation" aria-label="Mobile navigation">
            <nav className="flex flex-col space-y-1 pt-2">
              <Link to="/" className={`py-1 text-sm font-medium ${linkClass('/')}`} onClick={() => setIsMenuOpen(false)}>
                Home
              </Link>
              <Link to="/about" className={`py-1 text-sm font-medium ${linkClass('/about')}`} onClick={() => setIsMenuOpen(false)}>
                About
              </Link>

              {/* Services accordion */}
              <button
                type="button"
                className="flex items-center justify-between py-1 text-sm font-medium text-gray-700 hover:text-accent-600"
                onClick={() => setMobileServicesOpen((v) => !v)}
                aria-expanded={mobileServicesOpen}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="flex flex-col space-y-1 pl-3 border-l border-gray-200 ml-1">
                  <Link to="/assessment" className="py-1 text-sm text-accent-700 font-medium" onClick={() => setIsMenuOpen(false)}>
                    Free assessment →
                  </Link>
                  {serviceLinks.map((s) => (
                    <Link key={s.href} to={s.href} className="py-1 text-sm text-gray-700 hover:text-accent-600" onClick={() => setIsMenuOpen(false)}>
                      {s.label}
                    </Link>
                  ))}
                  <Link to="/services" className="py-1 text-sm text-accent-600 font-medium" onClick={() => setIsMenuOpen(false)}>
                    View all services →
                  </Link>
                </div>
              )}

              <Link to="/how-change-happens" className={`py-1 text-sm font-medium ${linkClass('/how-change-happens')}`} onClick={() => setIsMenuOpen(false)}>
                How Change Happens
              </Link>
              <Link to="/blog" className={`py-1 text-sm font-medium ${linkClass('/blog')}`} onClick={() => setIsMenuOpen(false)}>
                Blog
              </Link>
              <Link to="/contact" className={`py-1 text-sm font-medium ${linkClass('/contact')}`} onClick={() => setIsMenuOpen(false)}>
                Contact
              </Link>

              <div className="flex flex-col space-y-1 pt-2 border-t border-gray-200">
                <a
                  href="tel:07932139245"
                  className="flex items-center space-x-2 text-gray-600 text-sm"
                >
                  <Phone className="w-3 h-3" />
                  <span>07932 139245</span>
                </a>
                <a href="mailto:Sharon@sharonclare-hypnotherapy.com" className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Mail className="w-3 h-3" />
                  <span>Sharon@sharonclare-hypnotherapy.com</span>
                </a>
                <Link to="/book" className="btn-primary text-center mt-2 text-sm py-2" onClick={() => setIsMenuOpen(false)}>
                  Book Free Consultation
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
